import { useState, useRef, useCallback } from 'react'
import { get } from 'lodash'
import graphql from '../services/graphql'
import { GET_OFFER_DETAILS } from '../shoplist-app/shopping/queries'
import { OfferDetailsType } from '../shoplist-app/shopping/types/ShoppingPageType'
import logger from '../lib/logger'

/***
 *  Use custom hook to create a fucntion we can use to get all offerDetails by .
 *  See more here(https://reactjs.org/docs/hooks-custom.html)
 */

interface Props {
  honeyOfferId?: string
  honeyStoreId?: string
}

const useOfferDetailsModalData = ({ honeyOfferId, honeyStoreId }: Props) => {
  const [offerDetails, setOfferDetails] = useState<OfferDetailsType | null>(
    null
  )
  const [fetchLoading, setFetchLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | undefined>()
  // requestCountRef will help to determine if a new request was done.
  // If a new request was done, all previous request should be ignored and should not update the state
  // requestsCountRef will keep consitency on its value during the hook life.
  // On a request requestsCountRef will be increased by one
  // and requestCount will declared with requestsCountRef value
  // if by the end of the request requestCount and requestsCountRef are different
  // this means a new request was done and that this request (previosuly requested) is expired
  // and should be ignored.
  const requestsCountRef = useRef(0)
  const isRequestUnexpired = (requestCount: number) =>
    requestCount === requestsCountRef.current
  const increaseRequestCount = () => {
    requestsCountRef.current = requestsCountRef.current + 1
  }

  const fetchOfferDetails = useCallback(async () => {
    setFetchLoading(true)
    increaseRequestCount()
    const requestCount = requestsCountRef.current
    const query = GET_OFFER_DETAILS
    const variables = {
      honeyOfferId,
      honeyStoreId
    }
    try {
      const {
        data: { data, errors }
      } = await graphql(query, variables)
      if (isRequestUnexpired(requestCount)) {
        if (errors) {
          logger.error('useOfferDetailsModalDataHook', errors)
          throw new Error(JSON.stringify(errors))
        }
        setOfferDetails(get(data, 'uiShopping.offers.items[0]') || null)
      }
    } catch (err) {
      if (isRequestUnexpired(requestCount)) {
        setError(err)
      }
    } finally {
      if (isRequestUnexpired(requestCount)) {
        setFetchLoading(false)
      }
    }
  }, [honeyOfferId, honeyStoreId])

  return {
    fetchOfferDetails,
    fetchLoading,
    error,
    offerDetails
  }
}

export default useOfferDetailsModalData
